/**
 * 玩家回放 - 截图轮播
 *
 * 数据源: /api/v1/admin/playback/sessions/{w}/{p}/images
 * 单 player 一局通常 ≤ 10 张，使用 Image.PreviewGroup + 网格缩略图，点击放大。
 */

import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Empty,
  Image,
  Skeleton,
  Tag,
  Space,
  message,
  Tooltip,
} from 'antd'
import { ReloadOutlined, CameraOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { playbackApi } from '@/services/api'
import type { PlaybackImageItem } from '@/types/playback'

interface Props {
  worldId: string
  playerId: string
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'default',
  capturing: 'processing',
  uploaded: 'success',
  failed: 'error',
  dead_letter: 'error',
}

const ScreenshotCarousel: React.FC<Props> = ({ worldId, playerId }) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<PlaybackImageItem[]>([])

  const fetchImages = useCallback(async () => {
    setLoading(true)
    try {
      const response = await playbackApi.getImages(worldId, playerId)
      setItems(response.data?.items ?? [])
    } catch (error: any) {
      message.error(error?.message ?? '加载截图失败')
      console.error('fetchImages error:', error)
    } finally {
      setLoading(false)
    }
  }, [worldId, playerId])

  useEffect(() => {
    fetchImages()
  }, [fetchImages])

  const validImages = items.filter((it) => it.image_url)
  const invalidImages = items.filter((it) => !it.image_url)

  return (
    <Card
      title={
        <Space>
          <CameraOutlined />
          <span>回放截图</span>
          <Tag>{items.length}</Tag>
        </Space>
      }
      size="small"
      extra={
        <ReloadOutlined
          onClick={fetchImages}
          style={{ cursor: 'pointer' }}
        />
      }
    >
      {loading && items.length === 0 ? (
        <Skeleton active />
      ) : items.length === 0 ? (
        <Empty description="暂无截图" />
      ) : (
        <>
          <Image.PreviewGroup>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              {validImages.map((item) => (
                <div
                  key={`${item.session_slot_uuid}-${item.report_version}`}
                  style={{ position: 'relative' }}
                >
                  <Image
                    src={item.image_url!}
                    alt={`screenshot-${item.report_version}`}
                    style={{
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                    placeholder={
                      <div
                        style={{
                          height: 120,
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#999',
                        }}
                      >
                        加载中...
                      </div>
                    }
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      display: 'flex',
                      gap: 4,
                    }}
                  >
                    <Tag color={STATUS_COLOR[item.capture_status] ?? 'default'} style={{ margin: 0 }}>
                      {item.capture_status}
                    </Tag>
                  </div>
                  <Tooltip title={dayjs(item.occurred_at).format('YYYY-MM-DD HH:mm:ss')}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#666',
                        marginTop: 4,
                        textAlign: 'center',
                      }}
                    >
                      {dayjs(item.occurred_at).format('HH:mm:ss')}
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
          {invalidImages.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
              {invalidImages.length} 张截图暂未上传完成（状态：
              {invalidImages.map((it) => it.capture_status).join(', ')}）
            </div>
          )}
        </>
      )}
    </Card>
  )
}

export default ScreenshotCarousel